/**
 * Form Validation Utilities
 *
 * Pure functions for validating form data, especially for entity forms.
 * Used by GenericEntityUpsertDialog and other form-based components.
 * Entity-type-specific validation lives in ../validation/ (per-type files + ReusableValidation).
 */

import { SchemaAttribute, getAttributeName, getAttributeLabel } from './schemaAttributeUtils';
import { validatePolicyRule, type ValidationResult } from '../validation';

export { validatePolicyRule };

/**
 * Validate that required fields have values
 */
export function validateRequiredFields(
    formData: Record<string, any>,
    requiredAttributes: SchemaAttribute[]
): ValidationResult {
    for (const attr of requiredAttributes) {
        const name = getAttributeName(attr);
        // Form may key by plain name (DisplayName) or schema name ($.DisplayName); try both
        const plainName = getFormKeyFromAttrName(name);
        const value = formData[plainName] ?? formData[name];
        
        if (value === undefined || value === null || value === '' ||
            (Array.isArray(value) && value.length === 0)) {
            return {
                isValid: false,
                error: `${getAttributeLabel(attr)} is required`
            };
        }
    }
    
    return { isValid: true, error: null };
}

/** Re-export for callers that import from formValidationUtils */
export type { ValidationResult } from '../validation';

/**
 * Validate form data based on entity type
 */
export function validateFormData(
    formData: Record<string, any>,
    schemaAttributes: SchemaAttribute[],
    options: {
        isPolicyRule?: boolean;
        relativeTargetAttributeError?: string | null;
    } = {}
): ValidationResult {
    const { isPolicyRule = false, relativeTargetAttributeError = null } = options;
    
    // PolicyRule-specific validation
    if (isPolicyRule) {
        const policyResult = validatePolicyRule(formData, relativeTargetAttributeError);
        if (!policyResult.isValid) {
            return policyResult;
        }
    }
    
    // Validate required fields
    const requiredAttributes = schemaAttributes.filter((attr) => attr.required);
    return validateRequiredFields(formData, requiredAttributes);
}

/**
 * Check if a value is empty (null, undefined, empty string, empty array)
 */
export function isEmpty(value: any): boolean {
    if (value === undefined || value === null || value === '') {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
}

/**
 * Check if a value has changed from the original
 */
export function hasValueChanged(newValue: any, oldValue: any): boolean {
    return JSON.stringify(newValue) !== JSON.stringify(oldValue);
}

/**
 * Normalize a string value by trimming and handling prefixes
 */
export function normalizeAttributeValue(value: string, addPrefix: boolean = false): string {
    if (!value || typeof value !== 'string') return value;
    
    const trimmed = value.trim();
    
    if (addPrefix && !trimmed.startsWith('$.')) {
        return `$.${trimmed}`;
    }
    
    return trimmed;
}

/**
 * Strip the $. prefix from an attribute value
 */
export function stripAttributePrefix(value: string): string {
    if (!value || typeof value !== 'string') return value;
    return value.replace(/^\$\.?/, '');
}

/**
 * Normalize schema attribute name to form state key (strip leading "$.").
 * Form data must use plain keys (e.g. "DisplayName", "RequestorSet") so we never have both "$.X" and "X".
 */
export function getFormKeyFromAttrName(name: string | undefined): string {
    const s = (name ?? '').toString().trim();
    const plain = s.replace(/^\$\./, '') || s;
    return plain;
}

/**
 * Return any keys in formData that start with "$.".
 * Used in tests to assert form data never uses $. prefix (single source of truth per field).
 */
export function getFormDataKeysWithDollarPrefix(formData: Record<string, unknown>): string[] {
    if (!formData || typeof formData !== 'object') return [];
    return Object.keys(formData).filter((k) => (k ?? '').toString().startsWith('$.'));
}

/**
 * Add the $. prefix to an attribute value if not present
 */
export function addAttributePrefix(value: string): string {
    if (!value || typeof value !== 'string') return value;
    if (value.startsWith('$.')) return value;
    return `$.${value}`;
}

